import React, { useState, useMemo } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { Input } from "./ui/input";
import { Search } from "lucide-react";

/**
 * Reusable sortable table.
 * columns: [{ key, label, render?, align?, sortable?, accessor? }]
 * data: array of rows
 * searchable: array of keys to match search against
 */
export default function SortableTable({ columns, data, searchable = [], initialSort, testid = "sortable-table", emptyText = "No records." }) {
  const [sort, setSort] = useState(initialSort || { key: null, dir: "asc" });
  const [search, setSearch] = useState("");

  const toggleSort = (key) => {
    setSort((s) => {
      if (s.key !== key) return { key, dir: "asc" };
      if (s.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: "asc" };
    });
  };

  const filtered = useMemo(() => {
    let rows = data;
    if (search.trim() && searchable.length) {
      const q = search.toLowerCase();
      rows = rows.filter((r) =>
        searchable.some((k) => String(r[k] ?? "").toLowerCase().includes(q))
      );
    }
    if (sort.key) {
      const col = columns.find((c) => c.key === sort.key);
      const acc = col?.accessor || ((r) => r[sort.key]);
      rows = [...rows].sort((a, b) => {
        const av = acc(a), bv = acc(b);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === "number" && typeof bv === "number") return sort.dir === "asc" ? av - bv : bv - av;
        const as = String(av).toLowerCase(), bs = String(bv).toLowerCase();
        return sort.dir === "asc" ? as.localeCompare(bs) : bs.localeCompare(as);
      });
    }
    return rows;
  }, [data, search, sort, columns, searchable]);

  return (
    <div className="space-y-3" data-testid={testid}>
      {searchable.length > 0 && (
        <div className="relative max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid={`${testid}-search`}
            placeholder="Search..."
            className="pl-9 h-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}
      <div className="bg-white border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt">
            <tr>
              {columns.map((c) => {
                const sortable = c.sortable !== false;
                const active = sort.key === c.key;
                return (
                  <th
                    key={c.key}
                    className={`px-3 py-2 text-${c.align || "left"} font-semibold text-muted-foreground select-none ${sortable ? "cursor-pointer hover:bg-surface-alt/70" : ""}`}
                    onClick={sortable ? () => toggleSort(c.key) : undefined}
                    data-testid={`${testid}-th-${c.key}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {sortable && (
                        active
                          ? (sort.dir === "asc" ? <ChevronUp size={12} className="text-terracotta"/> : <ChevronDown size={12} className="text-terracotta"/>)
                          : <ChevronsUpDown size={12} className="opacity-30"/>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, i) => (
              <tr
                key={row._key || row.id || row.product_id || row.bill_id || row.user_id || row.session_id || row.phone || i}
                className={`border-t border-border ${row._onClick ? "cursor-pointer hover:bg-sand" : ""}`}
                onClick={row._onClick}
                data-testid={`${testid}-row-${i}`}
              >
                {columns.map((c) => (
                  <td key={c.key} className={`px-3 py-2 text-${c.align || "left"}`}>
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-muted-foreground">{emptyText}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-muted-foreground">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</div>
    </div>
  );
}
